import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserPanelPages.css";
import Navbar from "../Component/Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

/* ==============================
   FORMAT HELPERS
============================== */
const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const normalizeFavorite = (item = {}, index = 0) => ({
  id: item.id || item.favoriteId || item.favorite_id || `favorite-${index}`,
  itemName:
    item.itemName ||
    item.item_name ||
    item.domain_name ||
    item.domainName ||
    item.name ||
    "Saved Item",
  itemType: item.itemType || item.item_type || item.type || "domain",
  createdAt: item.createdAt || item.created_at || "",
  createdAtText:
    item.createdAtFormatted ||
    item.created_at_formatted ||
    formatDate(item.createdAt || item.created_at),
});

function FavoritesPage() {
  const navigate = useNavigate();

  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const redirectToSignIn = useCallback(() => {
    clearAuthStorage();
    navigate("/signin", { replace: true });
  }, [navigate]);

  /* ==============================
     PROTECTED FETCH
  ============================== */
  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const token = getToken();

      if (!token) {
        redirectToSignIn();
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        redirectToSignIn();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [redirectToSignIn]
  );

  /* ==============================
     LOAD FAVORITES
  ============================== */
  const fetchFavorites = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setStatus({ type: "", message: "" });

        const data = await authFetch("/favorites/my-favorites?page=1&limit=20");

        const safeFavorites = Array.isArray(data?.data)
          ? data.data.map(normalizeFavorite)
          : Array.isArray(data?.favorites)
          ? data.favorites.map(normalizeFavorite)
          : [];

        setFavorites(safeFavorites);
      } catch (err) {
        setFavorites([]);

        setStatus({
          type: "error",
          message: err.message || "Failed to fetch favorites.",
        });
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        fetchFavorites(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleFavoriteUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      setStatus({
        type: "success",
        message: payload?.message || "Favorites updated successfully.",
      });

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      redirectToSignIn();
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("favorite:update", handleFavoriteUpdate);
    socket.on("favorites:update", handleFavoriteUpdate);
    socket.on("favorite:status", handleFavoriteUpdate);

    socket.on("dashboard:update", handleRealtimeRefresh);
    socket.on("notification:new", handleRealtimeRefresh);
    socket.on("activity:new", handleRealtimeRefresh);
    socket.on("domain:status", handleRealtimeRefresh);
    socket.on("payment:status", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("favorite:update", handleFavoriteUpdate);
      socket.off("favorites:update", handleFavoriteUpdate);
      socket.off("favorite:status", handleFavoriteUpdate);

      socket.off("dashboard:update", handleRealtimeRefresh);
      socket.off("notification:new", handleRealtimeRefresh);
      socket.off("activity:new", handleRealtimeRefresh);
      socket.off("domain:status", handleRealtimeRefresh);
      socket.off("payment:status", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [fetchFavorites, redirectToSignIn]);

  const handleRefresh = () => {
    fetchFavorites(true);
  };

  return (
    <>
      <Navbar />

      <section className="user-page-section">
        <div className="user-page-container">
          <div className="user-page-head">
            <div>
              <h1>Saved Favorites</h1>
              <p>Your saved domain ideas, private to your account.</p>

              <div className="user-realtime-row">
                <div className="user-realtime-chip">
                  <span
                    className={
                      realtimeStatus === "connected"
                        ? "user-realtime-dot online"
                        : "user-realtime-dot offline"
                    }
                  />

                  <span>
                    {realtimeStatus === "connected"
                      ? "Realtime connected"
                      : realtimeStatus === "connecting"
                      ? "Connecting realtime..."
                      : "Realtime offline"}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="user-page-refresh-btn"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {status.message ? (
            <div className={`user-page-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="user-grid-list">
            {loading ? (
              <div className="user-page-card">
                <p className="user-page-muted">Loading favorites...</p>
              </div>
            ) : favorites.length === 0 ? (
              <div className="user-page-card">
                <p className="user-page-muted">No favorites found yet.</p>
              </div>
            ) : (
              favorites.map((item) => (
                <div className="mini-info-card" key={item.id}>
                  <span className="mini-info-badge">
                    {String(item.itemType || "domain").toUpperCase()}
                  </span>

                  <h3>{item.itemName}</h3>

                  <p>Added on {item.createdAtText}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default FavoritesPage;